/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { getInitiativeGroupTree } from '../../selectors/initiativeTree';
import { Tree, ReportingLevelInterface, ReportingLevelOption } from '../../types/tree';
import './styles.scss'

interface PortfolioSelectionProps {
  disabled?: boolean;
  placeholder: string;
  selectedPortfolioId?: string;
  handleSetPortfolio: (portfolioId: string) => void;
}

const createOptions = (reportingLevelTree: Tree) => {

  const hasReportingLevels = reportingLevelTree.children.length > 0;
  const maxNestedOptions = 6;
  const reportingLevelOptions: ReportingLevelOption[] = [];

  if (!hasReportingLevels) {
    return reportingLevelOptions;
  }

  const populateReportingLevelOptions = (level: ReportingLevelInterface, nested: number, parentId: string) => {
    reportingLevelOptions.push({
      value: level.id,
      label: level.name,
      parentId: parentId,
      isHidden: nested > maxNestedOptions,
      nested: nested,
    });
    if (level.children && level.children.length > 0) {
      level.children.forEach(child => populateReportingLevelOptions(child, nested + 1, level.id));
    }
  }
  reportingLevelTree.children.forEach(child => populateReportingLevelOptions(child, 1, reportingLevelTree.id));

  return reportingLevelOptions;
};

const PortfolioSelect = ({ placeholder, disabled, selectedPortfolioId, handleSetPortfolio }: PortfolioSelectionProps) => {
  const portfolioTree = useSelector(getInitiativeGroupTree);

  const reportingLevelOptions = createOptions(portfolioTree);

  const selectedPortfolio = selectedPortfolioId ? reportingLevelOptions.find(p => p.value === selectedPortfolioId) : null;

  const nestedPadding = 12;

  return <Select
    classNamePrefix='react-select'
    isDisabled={disabled}
    menuPlacement='auto'
    placeholder={placeholder}
    options={reportingLevelOptions}
    value={selectedPortfolio}
    styles={{
      option: (provided, state) => {
        return {
          ...provided,
          display: state.data.isHidden ? 'none' : 'inline-block',
          paddingLeft: `${(state.data.nested) * nestedPadding}px`,
          fontWeight: state.data.nested === 1 ? 'bold' : 'normal',
        }
      }
    }}
    onChange={(o) => handleSetPortfolio(o?.value ?? '')}
  />
}


export default PortfolioSelect;
